import React from 'react';
import { Hash, ArrowLeft } from 'lucide-react';
import { EvaluationSession } from '../../hooks/useEvaluationSessionData';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

interface EvaluationHeaderProps {
  evaluation: EvaluationSession | null;
  isLoading: boolean;
  error: string | null;
  onOpenKeypad: () => void;
  onBack?: () => void;
}

const EvaluationHeader: React.FC<EvaluationHeaderProps> = ({
  evaluation,
  isLoading,
  error,
  onOpenKeypad,
  onBack
}) => {
  const ageGroups = evaluation?.evaluation_session_age_groups.map(
    ag => `${ag.age_groups.group_name} ${ag.age_groups.sex === 'male' ? 'Boys' : 'Girls'}`
  ) || [];

  return (
    <header className="bg-blue-600 text-white shadow-lg sticky top-0 z-10">
      <div className="container mx-auto px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center min-w-0">
              {onBack && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onBack}
                  className="mr-1.5 shrink-0"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              {isLoading ? (
                <h1 className="text-base sm:text-lg font-semibold truncate">Soccer Placement Assessments</h1>
              ) : error ? (
                <div className="mt-1 text-sm text-red-200">{error}</div>
              ) : ageGroups.length > 0 ? (
                <div className="flex items-center gap-1.5 min-w-0">
                  <h1 className="text-base sm:text-lg font-semibold truncate">{ageGroups[0]}</h1>
                  {ageGroups.length > 1 && (
                    <Popover>
                      <PopoverTrigger>
                        <button type="button" className="focus:outline-none">
                          <Badge variant="outline" className="whitespace-nowrap text-xs py-0 h-5 px-1.5 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                            +{ageGroups.length - 1}
                          </Badge>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent side="bottom" align="start">
                        <div className="flex flex-wrap gap-2 p-2">
                          {ageGroups.map(g => (
                            <Badge key={g} variant="outline">
                              {g}
                            </Badge>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              ) : null}
          </div>
          <div className="flex items-center shrink-0">
            <button
              onClick={onOpenKeypad}
              className="bg-blue-500 hover:bg-blue-400 text-white px-3 py-1.5 rounded-md flex items-center gap-1.5 transition text-sm"
            >
              <Hash className="h-4 w-4" />
              <span>Quick Find</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default EvaluationHeader;
